import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, useMemo } from 'react'
import { RefreshCw, Sparkles, Trash2, Pencil, X } from 'lucide-react'
import { api, type SotFact, type SotFactInput } from '../services/api'
import { nanoAvailability, extractWithNano } from '../lib/nano'

// Stored URLs often lack a scheme (e.g. "asminternational.org/…"); without one
// the browser treats the href as relative and prepends the admin app origin.
function toAbsoluteUrl(url: string): string {
  const u = url.trim()
  return /^https?:\/\//i.test(u) ? u : `https://${u}`
}

const EMPTY_FORM: SotFactInput = {
  topic: '',
  owner: 'ASM',
  question: '',
  answer: '',
  status: 'confirmed',
  url: '',
  source_doc: '',
}

export default function SourceOfTruth() {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<SotFactInput>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [scrapeBusy, setScrapeBusy] = useState<string | null>(null)
  const [scrapeMsg, setScrapeMsg] = useState('')
  const [notice, setNotice] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['source-of-truth'],
    queryFn: () => api.getSourceOfTruth(),
  })
  const facts = data?.facts ?? []

  const saveMutation = useMutation({
    mutationFn: (fact: SotFactInput) => api.upsertSourceOfTruth(fact),
    onSuccess: () => {
      setNotice(editingId ? 'Fact updated in the knowledge base.' : 'Fact added to the knowledge base.')
      resetForm()
      queryClient.invalidateQueries({ queryKey: ['source-of-truth'] })
    },
    onError: (e: Error) => setNotice(`Error: ${e.message}`),
  })

  const deleteMutation = useMutation({
    mutationFn: (factId: string) => api.deleteSourceOfTruth(factId),
    onSuccess: () => {
      setNotice('Fact deleted from the knowledge base.')
      queryClient.invalidateQueries({ queryKey: ['source-of-truth'] })
    },
    onError: (e: Error) => setNotice(`Error: ${e.message}`),
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return facts
    return facts.filter(
      (f) =>
        f.topic.toLowerCase().includes(q) ||
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q),
    )
  }, [facts, search])

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  function startEdit(fact: SotFact) {
    setEditingId(fact.fact_id)
    setForm({
      topic: fact.topic,
      owner: fact.owner || 'ASM',
      question: fact.question,
      answer: fact.answer,
      status: fact.status || 'confirmed',
      url: fact.url || '',
      source_doc: fact.source_doc || '',
    })
  }

  useEffect(() => {
    if (!editingId) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saveMutation.isPending) resetForm()
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [editingId, saveMutation.isPending])

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!form.topic.trim() || !form.question.trim() || !form.answer.trim()) {
      setNotice('Error: topic, question, and answer are required.')
      return
    }
    setNotice('')
    saveMutation.mutate({ ...form, url: form.url.trim() ? toAbsoluteUrl(form.url) : '' })
  }

  // Fetch an ASM page (server-side, avoids CORS) and extract an answer with
  // on-device Gemini Nano. Returns the drafted answer, or raw text if Nano is
  // unavailable. `busyTag` identifies the working row ('form' or a fact_id).
  async function scrapeAndExtract(url: string, question: string, busyTag: string): Promise<string | null> {
    if (!url.trim()) {
      setScrapeMsg('Add a URL first.')
      return null
    }
    setScrapeBusy(busyTag)
    setScrapeMsg('Fetching page…')
    try {
      const data = await api.fetchUrlForSot(toAbsoluteUrl(url))
      const pageText = data.text || ''
      if (!pageText.trim()) throw new Error('Page had no readable text')

      const status = await nanoAvailability()
      if (status === 'no-api' || status === 'unavailable') {
        setScrapeMsg("Gemini Nano isn't available in this browser — filled raw page text instead; please trim it.")
        return pageText.slice(0, 2000)
      }
      setScrapeMsg(
        status === 'downloadable' || status === 'downloading'
          ? 'Downloading Gemini Nano model (first use)…'
          : 'Extracting with Gemini Nano…',
      )
      const answer = await extractWithNano(pageText, question, {
        onProgress: (pct) => setScrapeMsg(`Downloading Gemini Nano model… ${pct}%`),
      })
      setScrapeMsg(answer ? 'Draft ready — review and save.' : 'Nano returned nothing; try again.')
      return answer || pageText.slice(0, 2000)
    } catch (err) {
      setScrapeMsg(`Error: ${err instanceof Error ? err.message : 'Scrape failed'}`)
      return null
    } finally {
      setScrapeBusy(null)
    }
  }

  async function draftFromUrl() {
    const answer = await scrapeAndExtract(form.url, form.question, 'form')
    if (answer !== null) setForm((f) => ({ ...f, answer }))
  }

  async function refreshFromSource(fact: SotFact) {
    const answer = await scrapeAndExtract(fact.url, fact.question, fact.fact_id)
    if (answer !== null) {
      startEdit({ ...fact, answer })
      setScrapeMsg('Refreshed draft loaded into the editor — review and click Update.')
    }
  }

  return (
    <div className="p-6 space-y-6">
      {editingId && (
        <button
          type="button"
          aria-label="Close fact editor"
          className="fixed inset-0 z-40 cursor-default bg-black/60"
          onClick={() => !saveMutation.isPending && resetForm()}
        />
      )}
      {/* Add / edit form */}
      <form
        onSubmit={handleSave}
        role={editingId ? 'dialog' : undefined}
        aria-modal={editingId ? true : undefined}
        aria-labelledby={editingId ? 'source-of-truth-editor-title' : undefined}
        className={editingId
          ? 'card fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto border border-base-300 bg-base-100 shadow-2xl'
          : 'card max-w-2xl border border-base-300 bg-base-100 shadow-xl'}
      >
        <div className="card-body gap-4">
          <div className="flex items-center justify-between">
            <h2 id={editingId ? 'source-of-truth-editor-title' : undefined} className="card-title text-lg">
              {editingId ? 'Edit fact' : 'Add a verified fact'}
            </h2>
            {editingId && (
              <button type="button" onClick={resetForm} className="btn btn-ghost btn-sm gap-1">
                <X size={16} /> Cancel edit
              </button>
            )}
          </div>

          <label className="form-control w-full">
            <span className="label-text mb-1">Topic *</span>
            <input
              className="input input-bordered w-full"
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="e.g. membership, awards, foundation"
            />
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1">Owner</span>
            <input
              className="input input-bordered w-full"
              value={form.owner}
              onChange={(e) => setForm({ ...form, owner: e.target.value })}
              placeholder="ASM"
            />
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1">Question *</span>
            <input
              className="input input-bordered w-full"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="The question this fact answers"
            />
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1">Answer *</span>
            <textarea
              className="textarea textarea-bordered w-full min-h-32"
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              placeholder="The verified, authoritative answer Mat-E should ground on"
            />
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1">URL</span>
            <div className="flex gap-2">
              <input
                className="input input-bordered flex-1 min-w-0"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://www.asminternational.org/…"
              />
              <button
                type="button"
                onClick={draftFromUrl}
                disabled={scrapeBusy !== null || !form.url.trim()}
                title="Fetch the page and draft an answer with on-device Gemini Nano"
                className="btn btn-secondary gap-1 shrink-0"
              >
                <Sparkles size={16} />
                {scrapeBusy === 'form' ? 'Working…' : 'Fetch & draft'}
              </button>
            </div>
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1">Status</span>
            <select
              className="select select-bordered w-full"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="confirmed">confirmed</option>
              <option value="draft">draft</option>
              <option value="needs_review">needs_review</option>
            </select>
          </label>

          <label className="form-control w-full">
            <span className="label-text mb-1">Source doc</span>
            <input
              className="input input-bordered w-full"
              value={form.source_doc}
              onChange={(e) => setForm({ ...form, source_doc: e.target.value })}
              placeholder="optional reference"
            />
          </label>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saveMutation.isPending} className="btn btn-primary">
              {saveMutation.isPending ? 'Saving…' : editingId ? 'Update fact' : 'Add fact'}
            </button>
            {editingId && (
              <span className="text-xs opacity-60">
                Editing an existing fact — changing the topic or question creates a new entry.
              </span>
            )}
          </div>

          {scrapeMsg && (
            <p className={`text-sm ${scrapeMsg.startsWith('Error') ? 'text-error' : 'text-secondary'}`}>
              {scrapeMsg}
            </p>
          )}
          {notice && (
            <p className={`text-sm ${notice.startsWith('Error') ? 'text-error' : 'text-success'}`}>{notice}</p>
          )}
        </div>
      </form>

      {/* Existing facts */}
      <div className="card bg-base-100 shadow-xl border border-base-300">
        <div className="card-body gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="card-title text-lg">
              Knowledge base <span className="opacity-60 font-normal">({facts.length} facts)</span>
            </h2>
            <div className="flex items-center gap-2">
              <input
                className="input input-bordered input-sm w-64"
                placeholder="Filter by topic, question, answer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                className="btn btn-ghost btn-sm gap-1"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['source-of-truth'] })}
              >
                <RefreshCw size={15} /> Refresh
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 opacity-70">
              <span className="loading loading-spinner loading-sm" /> Loading knowledge base…
            </div>
          ) : error ? (
            <p className="text-error text-sm">{(error as Error).message}</p>
          ) : facts.length === 0 ? (
            <p className="opacity-70 text-sm">No facts yet. Add one above, or run the loader script to seed the index.</p>
          ) : (
            <div className="space-y-3">
              {filtered.map((fact) => (
                <div key={fact.fact_id} className="rounded-lg border border-base-300 bg-base-200/40 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="badge badge-primary badge-sm">{fact.topic || '—'}</span>
                        {fact.status && fact.status !== 'confirmed' && (
                          <span className="badge badge-warning badge-sm">{fact.status}</span>
                        )}
                        {fact.owner && <span className="text-xs opacity-50">{fact.owner}</span>}
                      </div>
                      <p className="font-medium break-words">{fact.question}</p>
                      <p className="text-sm opacity-80 mt-1 whitespace-pre-wrap break-words">{fact.answer}</p>
                      {fact.url && (
                        <a
                          href={toAbsoluteUrl(fact.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link link-primary text-xs mt-1 inline-block break-all"
                        >
                          {fact.url}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button onClick={() => startEdit(fact)} className="btn btn-ghost btn-xs gap-1">
                        <Pencil size={14} /> Edit
                      </button>
                      {fact.url && (
                        <button
                          onClick={() => refreshFromSource(fact)}
                          disabled={scrapeBusy !== null}
                          title="Re-fetch the source page and draft an updated answer with Gemini Nano"
                          className="btn btn-secondary btn-xs gap-1"
                        >
                          <RefreshCw size={14} />
                          {scrapeBusy === fact.fact_id ? 'Working…' : 'Refresh'}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (confirm('Delete this fact from the knowledge base? This cannot be undone.')) {
                            deleteMutation.mutate(fact.fact_id)
                          }
                        }}
                        className="btn btn-error btn-outline btn-xs gap-1"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
